import React from 'react'
import Select, { components } from 'react-select'
import { HugeiconsIcon } from '@hugeicons/react'
import { ArrowDown01Icon } from '@hugeicons/core-free-icons'

const CustomOption = (props) => (
  <components.Option {...props}>
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        gap: 8,
        caretColor: 'transparent',
        cursor: props.isDisabled ? 'not-allowed' : 'pointer',
        opacity: props.isDisabled ? 0.6 : 1
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {props.data.icon ?? null}
        <span style={{ caret: 'transparent', color: props.isDisabled ? '#9CA3AF' : 'inherit', whiteSpace: 'nowrap' }}>
          {props.data.label}
        </span>
      </div>
      {props.data.isLoading && (
        <div
          className='spinner'
          style={{
            width: 14,
            height: 14,
            borderWidth: 2,
            borderTopColor: '#2B2D42',
            flexShrink: 0,
            marginLeft: 'auto'
          }}
          role='status'
          aria-label='Loading'
        />
      )}
      {props.isDisabled && !props.data.isLoading && (
        <span
          style={{
            fontSize: '11px',
            color: '#9CA3AF',
            fontWeight: 400,
            fontStyle: 'normal',
            marginLeft: 'auto',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          (Unavailable)
        </span>
      )}
    </div>
  </components.Option>
)

// Default single-value renderer — mirrors CustomOption so a selected option's
// icon still shows once the menu closes, since react-select's own SingleValue
// only renders the label. Pass `singleValueComponent` to override entirely.
const CustomSingleValue = (props) => {
  return (
    <components.SingleValue {...props}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          color: props.isDisabled ? '#9CA3AF' : '#111827'
        }}
      >
        {props.data.showIcon ? props.data?.icon : null}
        <span>{props.data.label}</span>
      </div>
    </components.SingleValue>
  )
}

// Multi-value chip label with the full text as a native tooltip, so chips
// can be width-capped and ellipsised without hiding the label.
const CustomMultiValueLabel = (props) => (
  <div title={props.data.label} style={{ display: 'flex', minWidth: 0 }}>
    <components.MultiValueLabel {...props} />
  </div>
)

const IndicatorSeparator = (props) => (
  <components.IndicatorSeparator {...props} />
)

const DropdownIndicator = ({ icon, ...props }) => (
  <components.DropdownIndicator {...props}>
    <span
      style={{
        display: 'inline-flex',
        transform: `rotate(${props.selectProps.menuIsOpen ? 180 : 0}deg)`,
        transition: 'transform 0.15s ease',
        color: props.isDisabled ? '#9CA3AF' : '#6B7280',
        cursor: props.isDisabled ? 'not-allowed' : 'pointer'
      }}
    >
      {icon}
    </span>
  </components.DropdownIndicator>
)

const defaultStyle = {
  container: (base, state) => ({
    ...base,
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    pointerEvents: 'auto'
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '13px',
    color: '#9CA3AF',
    fontFamily: "'Inter', sans-serif"
  }),
  control: (base, state) => ({
    ...base,
    borderRadius: '8px',
    border: state.isDisabled
      ? '1px solid #E5E7EB'
      : (state.isFocused ? '1px solid #2B2D42' : '1px solid #D1D5DB'),
    backgroundColor: state.isDisabled ? '#F3F4F6' : '#FFFFFF',
    boxShadow: 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto',
    opacity: state.isDisabled ? 0.7 : 1,
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    fontWeight: 500,
    minHeight: '36px',
    height: '36px',
    '&:hover': {
      borderColor: state.isDisabled
        ? '#E5E7EB'
        : (state.isFocused ? '#2B2D42' : '#9CA3AF')
    }
  }),
  valueContainer: (base, state) => ({
    ...base,
    padding: '0 10px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto'
  }),
  clearIndicator: (base, state) => ({
    ...base,
    padding: '4px',
    color: state.isDisabled ? '#9CA3AF' : '#6B7280',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto'
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: '0 8px',
    color: state.isDisabled ? '#9CA3AF' : '#6B7280',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto'
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
    padding: '4px',
    backgroundColor: '#FFFFFF',
    zIndex: 100,
    marginTop: '4px',
    overflow: 'hidden'
  }),
  menuList: (base) => ({
    ...base,
    padding: 0
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    fontFamily: "'Inter', sans-serif",
    fontWeight: state.isSelected ? 500 : 400,
    color: state.isDisabled
      ? '#9CA3AF'
      : (state.isSelected ? '#0E7490' : (state.isFocused ? '#111827' : '#374151')),
    backgroundColor: state.isDisabled
      ? 'transparent'
      : (state.isSelected ? '#E6F4F6' : (state.isFocused ? '#F9FAFB' : 'transparent')),
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: state.isDisabled ? 'none' : 'auto',
    opacity: state.isDisabled ? 0.6 : 1,
    '&:active': {
      backgroundColor: state.isDisabled ? 'transparent' : '#E6F4F6'
    }
  }),
  singleValue: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    fontFamily: "'Inter', sans-serif",
    fontWeight: 500,
    color: state.isDisabled ? '#9CA3AF' : '#111827',
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    pointerEvents: 'auto',
    margin: 0
  })
}

const compactStyle = {
  container: (base, state) => ({
    ...base,
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    pointerEvents: 'auto'
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '13px',
    color: '#9CA3AF',
    fontFamily: "'Inter', sans-serif"
  }),
  control: (base, state) => ({
    ...base,
    minWidth: '130px',
    width: 'auto',
    minHeight: '36px',
    height: '36px',
    borderRadius: '8px',
    border: state.isDisabled
      ? '1px solid #E5E7EB'
      : (state.isFocused ? '1px solid #2B2D42' : '1px solid #D1D5DB'),
    backgroundColor: state.isDisabled ? '#F3F4F6' : '#FFFFFF',
    boxShadow: 'none',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto',
    opacity: state.isDisabled ? 0.7 : 1,
    fontFamily: "'Inter', sans-serif",
    fontSize: '13px',
    fontWeight: 500,
    '&:hover': {
      borderColor: state.isDisabled
        ? '#E5E7EB'
        : (state.isFocused ? '#2B2D42' : '#9CA3AF')
    }
  }),
  valueContainer: (base, state) => ({
    ...base,
    padding: '0 10px',
    height: '34px',
    display: 'flex',
    alignItems: 'center',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto'
  }),
  clearIndicator: (base, state) => ({
    ...base,
    padding: '4px',
    color: state.isDisabled ? '#9CA3AF' : '#6B7280',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto'
  }),
  dropdownIndicator: (base, state) => ({
    ...base,
    padding: '0 8px',
    color: state.isDisabled ? '#9CA3AF' : '#6B7280',
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: 'auto'
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '8px',
    border: '1px solid #E5E7EB',
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04)',
    padding: '4px',
    backgroundColor: '#FFFFFF',
    zIndex: 100,
    marginTop: '4px',
    overflow: 'hidden'
  }),
  menuList: (base) => ({
    ...base,
    padding: 0
  }),
  option: (provided, state) => ({
    ...provided,
    borderRadius: '6px',
    padding: '6px 10px',
    fontSize: '13px',
    fontFamily: "'Inter', sans-serif",
    fontWeight: state.isSelected ? 500 : 400,
    color: state.isDisabled
      ? '#9CA3AF'
      : (state.isSelected ? '#0E7490' : (state.isFocused ? '#111827' : '#374151')),
    backgroundColor: state.isDisabled
      ? 'transparent'
      : (state.isSelected ? '#E6F4F6' : (state.isFocused ? '#F9FAFB' : 'transparent')),
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    pointerEvents: state.isDisabled ? 'none' : 'auto',
    opacity: state.isDisabled ? 0.6 : 1,
    '&:active': {
      backgroundColor: state.isDisabled ? 'transparent' : '#E6F4F6'
    }
  }),
  singleValue: (provided, state) => ({
    ...provided,
    fontSize: '13px',
    fontFamily: "'Inter', sans-serif",
    fontWeight: 500,
    color: state.isDisabled ? '#9CA3AF' : '#111827',
    cursor: state.isDisabled ? 'not-allowed' : 'default',
    pointerEvents: 'auto',
    margin: 0
  })
}

// Per-option coloured chip styling — driven by `data.color` on each option
// (e.g. { key: 'active', label: 'Active', color: '#54AEBD' }).
// Kept separate from compactStyle so it applies to isMulti regardless of isCompact.
const multiValueColorStyle = {
  multiValue: (base, { data }) => ({
    ...base,
    justifyContent: 'space-between',
    paddingRight: '10px',
    paddingLeft: '10px',
    backgroundColor: data.color,
    borderRadius: 13
  }),
  multiValueLabel: (base) => ({
    ...base,
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    fontWeight: '500',
    color: '#fff'
  }),
  multiValueRemove: (base, { data }) => ({
    ...base,
    color: '#fff',
    ':hover': {
      backgroundColor: data.color,
      filter: 'brightness(0.85)',
      color: '#fff'
    }
  })
}

const mergeStyles = (...styleObjs) => {
  const validObjs = styleObjs.filter(Boolean)
  const keys = new Set(validObjs.flatMap(s => Object.keys(s || {})))
  const merged = {}
  keys.forEach((key) => {
    merged[key] = (base, state) => {
      let result = base
      validObjs.forEach((s) => {
        if (s && s[key]) {
          if (typeof s[key] === 'function') {
            result = s[key](result, state)
          } else if (typeof s[key] === 'object') {
            result = { ...result, ...s[key] }
          }
        }
      })
      return result
    }
  })
  return merged
}

const SelectWithLoader = ({
  className,
  defaultValue,
  value,
  onChange,
  options,
  styles,
  isDisabled,
  placeholder = undefined,
  isCompact = false,
  isSearchable = false,
  isMulti = false,
  showIndicatorSeparator = true,
  dropdownIndicator = <HugeiconsIcon icon={ArrowDown01Icon} />,
  singleValueComponent = CustomSingleValue,
  menuPlacement = 'auto'
}) => {
  const base = isCompact ? compactStyle : defaultStyle
  const mergedWithCustom = styles ? mergeStyles(base, styles) : base
  const resolvedStyles = isMulti ? mergeStyles(mergedWithCustom, multiValueColorStyle) : mergedWithCustom

  const selectComponents = {
    IndicatorSeparator: (indicatorProps) => showIndicatorSeparator ? <IndicatorSeparator {...indicatorProps} /> : null,
    Option: CustomOption,
    SingleValue: singleValueComponent,
    MultiValueLabel: CustomMultiValueLabel
  }

  if (dropdownIndicator) {
    selectComponents.DropdownIndicator = props => (
      <DropdownIndicator {...props} icon={dropdownIndicator} />
    )
  }

  return (
    <Select
      className={className}
      isMulti={isMulti}
      isSearchable={isSearchable}
      menuPlacement={menuPlacement}
      styles={resolvedStyles}
      placeholder={placeholder}
      isOptionDisabled={option => option.isDisabled}
      options={options}
      value={value}
      defaultValue={defaultValue}
      components={selectComponents}
      isDisabled={isDisabled}
      onChange={onChange}
    />
  )
}

export default SelectWithLoader
